// useEventConfig — subscribe to /config/event for startsAt timestamp.
//
// Used by AuthGate to gate Countdown vs main routes. Per Eng-D7: if the
// snapshot errors or the doc is missing we fail OPEN (no Countdown) so a
// Firestore Rules typo can't lock learners out of the app entirely.
//
// Important: only attach the snapshot AFTER firebase/auth reports a user.
// Otherwise the very first onSnapshot call races the anon sign-in, gets
// permission-denied from Rules, falls into fail-open and accidentally
// bypasses the Countdown.

import { configEventDoc, getFirebase, useFirestoreDoc } from "@goodbye/firebase";
import type { ConfigEventDoc } from "@goodbye/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useEffect, useMemo, useState } from "react";

export interface EventConfigState {
  /** ms since epoch, or null when unknown / fail-open */
  startsAtMs: number | null;
  /** Active event cohort, e.g. "2025" / "2026". Defaults "2025" when missing. */
  cohortId: string;
  loading: boolean;
}

export function useEventConfig(): EventConfigState {
  const [hasAuthUser, setHasAuthUser] = useState(
    () => getFirebase().auth.currentUser !== null,
  );

  useEffect(() => {
    if (hasAuthUser) return;
    const unsub = onAuthStateChanged(getFirebase().auth, (u) => {
      if (u) setHasAuthUser(true);
    });
    return () => unsub();
  }, [hasAuthUser]);

  const ref = useMemo(() => (hasAuthUser ? configEventDoc() : null), [hasAuthUser]);
  const snap = useFirestoreDoc<ConfigEventDoc>(ref);

  // Still waiting for auth → keep AuthGate in its loading branch.
  if (!hasAuthUser) return { startsAtMs: null, cohortId: "2025", loading: true };

  // Fail-open: error OR missing doc → startsAtMs=null → AuthGate lets through.
  if (snap.loading) return { startsAtMs: null, cohortId: "2025", loading: true };
  if (snap.error || !snap.data) return { startsAtMs: null, cohortId: "2025", loading: false };
  return {
    startsAtMs: snap.data.startsAt.toMillis(),
    cohortId: snap.data.cohortId ?? "2025",
    loading: false,
  };
}
